import React, { useEffect, useState, useCallback } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  Button, 
  Space,
  Tag,
  Typography,
  Divider
} from 'antd';
import { 
  PlusOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FolderOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import todoCategoryService from '../../../services/todoCategoryService';
import TodoCategoryManage from './TodoCategoryManage';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const TodoForm = ({ 
  visible, 
  onCancel, 
  onSubmit, 
  title = "Add Todo", 
  initialValues = null,
  loading = false,
  categories = [],
  onCategoriesRefresh
}) => {
  const [form] = Form.useForm();
  const [localCategories, setLocalCategories] = useState([]);
  const [manageCategoriesVisible, setManageCategoriesVisible] = useState(false);

  const effectiveCategories = categories?.length ? categories : localCategories;

  const loadCategories = useCallback(async () => {
    try {
      const response = await todoCategoryService.getCategories();
      const result = response?.data || response;
      const list = (result?.data && Array.isArray(result.data)) ? result.data : (Array.isArray(result) ? result : []);
      setLocalCategories(list);
    } catch (e) {
      console.error('Failed to load categories', e);
    }
  }, []);

  useEffect(() => {
    if (visible && effectiveCategories.length === 0) {
      loadCategories();
    }
  }, [visible, effectiveCategories.length, loadCategories]);

  useEffect(() => {
    if (visible) {
      if (initialValues) {
        // Convert dates to dayjs objects for the form; use category id (public_id) for category_id
        const rawDue = initialValues.due_date ?? initialValues.dueDate;
        const formValues = {
          ...initialValues,
          dueDate: rawDue ? dayjs(rawDue) : null,
          category_id: initialValues.category?.id ?? initialValues.category_id ?? null
        };
        form.setFieldsValue(formValues);
      } else {
        form.resetFields();
      }
    }
  }, [visible, initialValues, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Convert dayjs to ISO string for API
      const todoData = {
        ...values,
        due_date: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : null,
        category_id: values.category_id || null,
        completed: initialValues?.completed || false,
        starred: initialValues?.starred || false
      };

      await onSubmit(todoData);
      form.resetFields();
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Add form field change listener for preview
  const [previewData, setPreviewData] = useState({});

  const handleFormChange = (changedFields, allFields) => {
    setPreviewData(allFields);
  };

  const priorityOptions = [
    {
      value: 'high',
      label: 'High',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      color: 'red'
    },
    {
      value: 'medium',
      label: 'Medium',
      icon: <ClockCircleOutlined style={{ color: '#faad14' }} />,
      color: 'orange'
    },
    {
      value: 'low',
      label: 'Low',
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      color: 'green'
    }
  ];

  const categoryOptions = effectiveCategories.map((c) => ({
    value: c.id,
    label: c.name,
    color: c.color || 'default'
  }));

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          onClick={handleSubmit}
          loading={loading}
        >
          {initialValues ? 'Update' : 'Create'}
        </Button>
      ]}
      width={600}
      destroyOnHidden
    >
      <Form
        form={form}
        size="large"
        layout="vertical"
        initialValues={{
          priority: 'medium',
          category_id: undefined,
        }}
        onFieldsChange={handleFormChange}
      >
        {/* Title */}
        <Form.Item
          name="title"
          label="Title"
          rules={[
            { required: true, message: 'Please enter a title' },
            { max: 100, message: 'Title must be less than 100 characters' }
          ]}
        >
          <Input 
            placeholder="Enter todo title"
            maxLength={100}
            showCount
          />
        </Form.Item>




        

{/* Priority and Category */}
<div style={{ display: 'flex', gap: 16 }}>
  <Form.Item
    name="priority"
    label="Priority"
    style={{ flex: 1 }}
  >
    <Select placeholder="Select priority">
      {priorityOptions.map(option => (
        <Option key={option.value} value={option.value}>
          <Space>
            {option.icon}
            {option.label}
          </Space>
        </Option>
      ))}
    </Select>
  </Form.Item>

  <Form.Item
    name="category_id"
    label={
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <span>Category</span>
        <Button
          type="link"
          onClick={() => setManageCategoriesVisible(true)}
          size="small"
          style={{ padding: 0 }}
        >
          Manage categories
        </Button>
      </Space>
    }
    style={{ flex: 1 }}
  >
    <Select placeholder="Select category" allowClear>
      {categoryOptions.map(option => (
        <Option key={option.value} value={option.value}>
          <Tag color={option.color} style={{ margin: 0 }}>
            {option.label}
          </Tag>
        </Option>
      ))}
    </Select>
  </Form.Item>
</div>

<TodoCategoryManage
  visible={manageCategoriesVisible}
  onClose={() => setManageCategoriesVisible(false)}
  categories={effectiveCategories}
  onRefresh={async () => {
    await loadCategories();
    onCategoriesRefresh?.();
  }}
/>

{/* Due Date */}
<Form.Item
  name="dueDate"
  label={
    <Space>
      <CalendarOutlined />
      Due Date
    </Space>
  }
>
  <DatePicker
    placeholder="Select due date"
    style={{ width: '100%' }}
    showTime={false}
    format="YYYY-MM-DD"
    disabledDate={(current) => {
      // Disable past dates
      return current && current < dayjs().startOf('day');
    }}
  />
</Form.Item>


        {/* Description */}
        <Form.Item
          name="description"
          label="Description"
          rules={[
            { max: 500, message: 'Description must be less than 500 characters' }
          ]}
        >
          <TextArea
            placeholder="Enter todo description (optional)"
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>

 

        {/* Preview */}
        {/* <Form.Item label="Preview">
          <div style={{ 

          }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              This is how your todo will appear in the list
            </Text>
            <div style={{ marginTop: 8 }}>
              {previewData.title && (
                <div style={{ fontWeight: 500 }}>
                  {previewData.title}
                </div>
              )}
              {previewData.description && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                  {previewData.description}
                </div>
              )}
              <div style={{ marginTop: 8 }}>
                {previewData.priority && (
                  <Tag 
                    color={priorityOptions.find(p => p.value === previewData.priority)?.color}
                    size="small"
                    style={{ marginRight: 8 }}
                  >
                    {previewData.priority}
                  </Tag>
                )}
                {previewData.category_id && (
                  <Tag 
                    color={categoryOptions.find(c => c.value === previewData.category_id)?.color}
                    size="small"
                    style={{ marginRight: 8 }}
                  >
                    {categoryOptions.find(c => c.value === previewData.category_id)?.label}
                  </Tag>
                )}
                {previewData.tags?.map(tag => (
                  <Tag key={tag} size="small" style={{ marginRight: 4 }}>
                    {tag}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
        </Form.Item> */}
      </Form>
    </Modal>
  );
};

export default TodoForm; 